import { memo } from 'react';

import { CREHANA_BASE_URL_SEO_PRD } from '@crehana/constants';
import { useTranslation } from '@crehana/i18n';
import type { CountryCode } from '@crehana/ts-types';
import Head from 'next/head';

import type { TCourse } from '../types';
import getBaseUrl from '../utils/getBaseUrl';

type Data = {
  [key in 'course' | 'product']:
    | Record<string, string | Record<string, string>>
    | undefined;
};

const getStructuredData = (
  course: TCourse,
  countryCode?: CountryCode | null,
) => {
  const description = course.metaDescription;

  const baseUrl = getBaseUrl();
  const courseUrl = baseUrl + course.url;

  const company = {
    '@context': 'http://schema.org/',
    '@type': 'Organization',
    name: 'CREHANA',
    logo: 'https://static.crehana.com/static/img/logo/logo-crehana.png',
    url: `${CREHANA_BASE_URL_SEO_PRD}/${countryCode}/`,
  };

  const data: Data = {
    course: {
      '@context': 'http://schema.org/',
      '@type': 'Course',
      name: course.title,
      description,
      provider: company,
    },
    product: {
      '@context': 'http://schema.org/',
      '@type': 'Product',
      productID: `crehana-course-${course.originalId}`,
      image: course.promoImage!,
      name: course.title,
      description,
      url: courseUrl,
      brand: company,
      manufacturer: {
        '@context': 'http://schema.org/',
        '@type': 'Organization',
        name: 'CREHANA',
        logo: 'https://static.crehana.com/static/img/logo/logo-crehana.png',
        url: `${CREHANA_BASE_URL_SEO_PRD}/${countryCode}/`,
      },
      category: '',
      sku: `Course-${course.originalId}`,
    },
  };

  return data;
};

type SEOStructuredDataProps = {
  course: TCourse;
};

export const SEOStructuredData: React.FC<SEOStructuredDataProps> = ({
  course,
}) => {
  const { countryCode } = useTranslation();
  const structuredData = getStructuredData(course, countryCode);

  return (
    <Head>
      {Object.values(structuredData).map((value, index) => (
        <script
          // biome-ignore lint/suspicious/noArrayIndexKey: stable list order, index key acceptable here
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(value) }}
        />
      ))}
    </Head>
  );
};

export default memo(SEOStructuredData);
